/**
 * Upsell modal fired when the user clicks a locked tier chip OR runs out of
 * credits on a paid tier. See docs/pricing/line-tier-integration-plan.md §7g.
 *
 * Session-scoped dismissal cooldown: once dismissed, don't reopen for the
 * same tier within 7 days. Persisted in localStorage keyed by tier.
 */

import { useMemo } from 'react'
import { AppDialog } from '../Common/AppDialog'
import { AI_TIER_CATALOG } from '../../utils/aiTierCatalog'
import type { AiTierId } from '../../types/ai'

const COOLDOWN_KEY = 'timelining_tier_upsell_cooldown'
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000

type CooldownStore = Partial<Record<AiTierId, number>>

const readCooldownStore = (): CooldownStore => {
  if (typeof window === 'undefined') return {}
  try {
    return JSON.parse(window.localStorage.getItem(COOLDOWN_KEY) ?? '{}') as CooldownStore
  } catch {
    return {}
  }
}

const writeCooldownStore = (store: CooldownStore) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(COOLDOWN_KEY, JSON.stringify(store))
}

export const isTierUpsellOnCooldown = (tier: AiTierId): boolean => {
  const store = readCooldownStore()
  const last = store[tier]
  return typeof last === 'number' && Date.now() - last < COOLDOWN_MS
}

export const recordTierUpsellDismissal = (tier: AiTierId) => {
  const store = readCooldownStore()
  store[tier] = Date.now()
  writeCooldownStore(store)
}

export interface AiTierUpsellModalProps {
  open: boolean
  /** The tier the user attempted (locked chip clicked, or quota exhausted). */
  attemptedTier: AiTierId
  /** Trigger source, used by analytics + copy nuance. */
  trigger: 'locked_chip' | 'quota_exhausted'
  onUpgrade: () => void
  onBuyCredits: () => void
  onDismiss: () => void
}

export const AiTierUpsellModal = ({
  open,
  attemptedTier,
  trigger,
  onUpgrade,
  onBuyCredits,
  onDismiss,
}: AiTierUpsellModalProps) => {
  const descriptor = AI_TIER_CATALOG[attemptedTier]

  const { title, message } = useMemo(() => {
    if (trigger === 'quota_exhausted') {
      return {
        title: `You're out of credits for ${descriptor.longLabel}`,
        message: (
          <div>
            <p style={{ marginTop: 0 }}>
              Buy a credit pack to keep drafting, or upgrade your plan for a bigger monthly grant.
            </p>
            <small style={{ opacity: 0.7 }}>{descriptor.upsellPitch}</small>
          </div>
        ),
      }
    }
    return {
      title: `${descriptor.longLabel} unlocks deeper reasoning`,
      message: (
        <div>
          <p style={{ marginTop: 0 }}>
            {descriptor.upsellPitch}
          </p>
          <small style={{ opacity: 0.7 }}>
            Upgrade your plan to include this tier in your monthly grant, or buy a credit pack for one-off use.
          </small>
        </div>
      ),
    }
  }, [attemptedTier, descriptor, trigger])

  const handleDismiss = () => {
    recordTierUpsellDismissal(attemptedTier)
    onDismiss()
  }

  return (
    <AppDialog
      open={open}
      title={title}
      message={message}
      actions={[
        { label: 'Upgrade plan', variant: 'primary', onClick: onUpgrade, autoFocus: true },
        { label: 'Buy credits instead', variant: 'ghost', onClick: onBuyCredits },
        { label: 'Not now', variant: 'ghost', onClick: handleDismiss },
      ]}
      onDismiss={handleDismiss}
    />
  )
}
